import Header from "../components/Header";
import Footer from "../components/Footer";

function Titre() {
  return (
    <div className="relative text-center w-full mx-auto h-[250px] bg-cover overflow-hidden">
      <div className="px-6 relative flex items-center justify-center min-h-[250px]">
        <h1 className="font-comme font-bold text-[36px] md:text-[48px] lg:text-[60px] leading-tight text-center text-gris-sombre dark:text-blanc-sombre max-w-5xl w-full">
          L'<span className="text-bleu-je">É</span>cole <span className="text-bleu-je">N</span>ationale <span className="text-bleu-je">S</span>upérieure de <span className="text-bleu-je">C</span>ognitique
        </h1>
      </div>
    </div>
  );
}

function Paragraphe({ titre, children }) {
  return (
    <div className="flex flex-col items-center justify-center max-w-5xl mx-auto my-10 px-5">
      <h2 className="text-[26px] md:text-[32px] font-comme font-semibold text-gris-sombre dark:text-blanc-sombre mb-4 text-center px-4 sm:px-6 md:px-0">{titre}</h2>
      <div className="text-[16px] md:text-[18px] font-comme text-gris-sombre dark:text-blanc-sombre leading-relaxed text-justify max-w-4xl">{children}</div>
    </div>
  );
}

function Photos() {
  const images = [
    { src: "/images/patio.avif", alt: "Patio de l'ENSC" },
    { src: "/images/foyer.avif", alt: "Foyer de l'ENSC" },
    { src: "/images/exterieur.avif", alt: "Vue extérieure de l'ENSC" }
  ];

  return (
    <div className="py-12 bg-white dark:bg-gris-sombre">
      <h2 className="text-center font-comme font-semibold text-[28px] md:text-[34px] text-gris-sombre dark:text-blanc-sombre mb-8">
        L’<span className="text-bleu-je">ENSC</span> en images
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto px-6">
        {images.map((img, i) => (
          <div key={i} className="overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
            <img src={img.src} alt={img.alt} className="w-full h-[220px] object-cover hover:scale-110 transition-transform duration-500" loading="lazy"/>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChiffresCles() {
  const stats = [
    { donnee: "≈ 250", titre: "élèves-ingénieurs" },
    { donnee: "14", titre: "enseignants et enseignants-chercheurs" },
    { donnee: "+ de 80", titre: "intervenants d'entreprises et d'industries" },
    { donnee: "3", titre: "diplômes universitaires" },
    { donnee: "7", titre: "entreprises hébergées" },
    { donnee: "6", titre: "écoles du groupe Bordeaux INP" },
    { donnee: "> 80", titre: "diplômés chaque année" },
    { donnee: "33 140 €", titre: "salaire moyen à l’embauche" }
  ];

  return (
    <div className="bg-gris-clair dark:bg-gris-sombre py-6 mt-8">
      <h2 className="text-center font-comme font-semibold text-[28px] md:text-[34px] text-gris-sombre dark:text-blanc-sombre mb-8">
        Quelques <span className="text-bleu-je">chiffres clés</span>
      </h2>
      <div className="flex flex-wrap justify-center gap-6 px-6 max-w-5xl mx-auto">
        {stats.map((s, i) => (
          <div key={i} className="flex flex-col items-center bg-white dark:bg-blanc-sombre rounded-xl shadow-md p-6 w-[300px] md:w-[220px] transition-transform duration-200 hover:scale-105">
            <p className="text-bleu-je font-bold text-[28px] md:text-[36px]">{s.donnee}</p>
            <p className="text-gris-sombre font-comme text-[16px] text-center">{s.titre}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ENSC() {
  return (
    <div className="dark:bg-fond-sombre">
      <Header/>
      <Titre/>

      <Paragraphe titre="Qu’est-ce que la cognitique ?">
        <p className="px-4 sm:px-6 md:px-0">
          La <strong>cognitique</strong> est la discipline qui étudie et conçoit les
          interactions entre l’humain et les systèmes complexes : interface,
          intelligence artificielle, ergonomie, psychologie cognitive, traitement
          de l’information, et plus encore.
        </p>
        <p className="mt-3 px-4 sm:px-6 md:px-0">
          Elle place l’utilisateur au centre de la conception des technologies,
          pour garantir performance, confort et éthique dans l’usage.
        </p>
      </Paragraphe>

      <Photos/>

      <Paragraphe titre="Une école d’ingénieurs unique">
        <p className="px-4 sm:px-6 md:px-0">
          L'<a href="https://ensc.bordeaux-inp.fr" target="_blank" rel="noreferrer"
          className="text-blue-600 underline">École Nationale Supérieure de
          Cognitique</a> est une école publique d’ingénieurs du groupe <a
          href="https://www.bordeaux-inp.fr" target="_blank" rel="noreferrer"
          className="text-blue-600 underline">Bordeaux INP</a>. Elle forme des
          ingénieurs à double compétence : technologies numériques et
          compréhension de l’humain.
        </p>
        <p className="mt-3 px-4 sm:px-6 md:px-0">
          Les élèves y développent des compétences en informatique, intelligence
          artificielle, ergonomie, psychologie, UX, traitement du signal, gestion
          de projet et conception centrée utilisateur.
        </p>
      </Paragraphe>

      <Paragraphe titre="Une pédagogie centrée sur l’humain">
        <p className="px-4 sm:px-6 md:px-0">
          Le cursus met l’accent sur l’interdisciplinarité et les projets concrets
          menés en partenariat avec des entreprises et laboratoires de recherche.
        </p>
        <p className="mt-3 px-4 sm:px-6 md:px-0">
          Les étudiants travaillent sur des thématiques variées : systèmes
          intelligents, interaction homme-machine, neuro-ergonomie ou encore
          robotique.
        </p>
      </Paragraphe>

      <ChiffresCles/>

      <div className="mb-16">
        <Paragraphe titre="À propos d'i2c">
          <p className="px-4 sm:px-6 md:px-0">
            L’ENSC héberge notre Junior-Entreprise Ingénierie et Conseil en
            Cognitique (i2c). Cette collaboration renforce le lien entre le
            monde académique et professionnel, enrichit la formation des
            élèves-ingénieurs et permet de valoriser les savoir-faire de l’école.
          </p>
        </Paragraphe>
      </div>

      <Footer/>
    </div>
  );
}

export default ENSC;